import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { formatApiMessage } from '@/api/client';
import { type TemplateView, templatesApi } from '@/api/templates.api';
import { AdminLayout } from '@/components/layout/AdminLayout';

/**
 * Approval Matrix admin — single page where the admin sees every template
 * tagged with trigger codes, lays out the approval chain side-by-side, and
 * edits in place. Templates without trigger codes are still listed so the
 * admin can opt them into the matrix from here without bouncing to /templates.
 *
 * The matrix is the user-facing artefact behind "per-document-type approval
 * chain" from the customer brief: matched codes auto-apply the recipient
 * sequence in the new-envelope wizard, so end users don't pick chains.
 */
export function AdminApprovalMatrixPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['templates', 'list'],
    queryFn: () => templatesApi.list(),
  });

  // Templates with at least one trigger code go to the matrix table;
  // the rest land below in a "not configured" list so the admin sees them.
  const matrixed = templates.filter(
    (t) => t.triggerType || t.triggerDept || t.triggerForm,
  );
  const unconfigured = templates.filter(
    (t) => !t.triggerType && !t.triggerDept && !t.triggerForm,
  );

  // Conflict detection: two templates can't share the same trigger triple
  // because the wizard wouldn't know which one to auto-apply.
  const conflictKeys = new Set<string>();
  const seen = new Map<string, string>();
  for (const tpl of matrixed) {
    const key = `${tpl.triggerType ?? ''}|${tpl.triggerDept ?? ''}|${tpl.triggerForm ?? ''}`;
    if (seen.has(key) && seen.get(key) !== tpl.id) {
      conflictKeys.add(key);
    } else {
      seen.set(key, tpl.id);
    }
  }

  return (
    <AdminLayout
      title={t('admin.approval_matrix_title')}
      subtitle={t('admin.approval_matrix_subtitle')}
    >
      {isLoading ? (
        <div className="card text-center py-12 text-muted">{t('common.loading')}</div>
      ) : (
        <>
          <div className="card !p-0 mb-5">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900 m-0">
                {t('admin.matrix_active_title')} ({matrixed.length})
              </h3>
              <Link to="/templates" className="text-[12px] text-primary">
                {t('admin.matrix_manage_templates')} →
              </Link>
            </div>

            {matrixed.length === 0 ? (
              <div className="px-4 py-8 text-center text-muted text-[13px]">
                {t('admin.matrix_empty_hint')}
              </div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('admin.matrix_type')}</th>
                    <th>{t('admin.matrix_dept')}</th>
                    <th>{t('admin.matrix_form')}</th>
                    <th>{t('admin.matrix_template')}</th>
                    <th>{t('admin.matrix_chain')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {matrixed.map((tpl) => {
                    const key = `${tpl.triggerType ?? ''}|${tpl.triggerDept ?? ''}|${tpl.triggerForm ?? ''}`;
                    const inConflict = conflictKeys.has(key);
                    return (
                      <MatrixRow
                        key={tpl.id}
                        template={tpl}
                        inConflict={inConflict}
                        onSaved={() => qc.invalidateQueries({ queryKey: ['templates', 'list'] })}
                      />
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {unconfigured.length > 0 && (
            <div className="card !p-0">
              <div className="px-4 py-3 border-b border-border">
                <h3 className="text-base font-semibold text-gray-900 m-0">
                  {t('admin.matrix_unconfigured_title')} ({unconfigured.length})
                </h3>
                <p className="text-[12px] text-muted mt-1">
                  {t('admin.matrix_unconfigured_hint')}
                </p>
              </div>
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('admin.matrix_template')}</th>
                    <th>{t('admin.matrix_chain')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {unconfigured.map((tpl) => (
                    <UnconfiguredRow
                      key={tpl.id}
                      template={tpl}
                      onSaved={() => qc.invalidateQueries({ queryKey: ['templates', 'list'] })}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
}

function MatrixRow({
  template,
  inConflict,
  onSaved,
}: {
  template: TemplateView;
  inConflict: boolean;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [type, setType] = useState(template.triggerType ?? '');
  const [dept, setDept] = useState(template.triggerDept ?? '');
  const [form, setForm] = useState(template.triggerForm ?? '');

  const save = useMutation({
    mutationFn: () =>
      templatesApi.update(template.id, {
        triggerType: type || null,
        triggerDept: dept || null,
        triggerForm: form || null,
      }),
    onSuccess: () => {
      onSaved();
      setEditing(false);
      toast.success(t('admin.matrix_saved'));
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const clearTrigger = useMutation({
    mutationFn: () =>
      templatesApi.update(template.id, {
        triggerType: null,
        triggerDept: null,
        triggerForm: null,
      }),
    onSuccess: onSaved,
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  return (
    <tr className={inConflict ? 'bg-warning-bg/40' : ''}>
      {editing ? (
        <>
          <td>
            <input
              autoFocus
              className="text-[12px] border border-border rounded px-1.5 py-1 w-16"
              maxLength={12}
              value={type}
              placeholder="INT"
              onChange={(e) => setType(e.target.value.toUpperCase())}
            />
          </td>
          <td>
            <input
              className="text-[12px] border border-border rounded px-1.5 py-1 w-16"
              maxLength={12}
              value={dept}
              placeholder="GRC"
              onChange={(e) => setDept(e.target.value.toUpperCase())}
            />
          </td>
          <td>
            <input
              className="text-[12px] border border-border rounded px-1.5 py-1 w-16"
              maxLength={12}
              value={form}
              placeholder="FRM"
              onChange={(e) => setForm(e.target.value.toUpperCase())}
            />
          </td>
        </>
      ) : (
        <>
          <td><CodePill code={template.triggerType} /></td>
          <td><CodePill code={template.triggerDept} /></td>
          <td><CodePill code={template.triggerForm} /></td>
        </>
      )}
      <td>
        <div className="font-medium text-gray-900">{template.title}</div>
        <div className="text-[11px] text-muted">{t(`envelope.workflow_${template.workflowType}`)}</div>
        {inConflict && (
          <div className="text-[10px] text-danger mt-1">{t('admin.matrix_conflict')}</div>
        )}
      </td>
      <td>
        <ChainPreview template={template} />
      </td>
      <td className="text-right whitespace-nowrap">
        {editing ? (
          <>
            <button
              onClick={() => save.mutate()}
              disabled={save.isPending}
              className="text-[12px] text-primary px-2"
            >
              ✓
            </button>
            <button
              onClick={() => {
                setEditing(false);
                setType(template.triggerType ?? '');
                setDept(template.triggerDept ?? '');
                setForm(template.triggerForm ?? '');
              }}
              className="text-[12px] text-muted px-2"
            >
              ×
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() => setEditing(true)}
              className="btn btn-ghost btn-sm"
              title={t('admin.matrix_edit_codes')}
            >
              ✎
            </button>
            <button
              onClick={() => {
                if (confirm(t('admin.matrix_clear_confirm'))) clearTrigger.mutate();
              }}
              className="btn btn-ghost btn-sm text-danger"
              title={t('admin.matrix_clear')}
            >
              ✕
            </button>
            <Link
              to="/templates"
              className="btn btn-ghost btn-sm"
              title={t('admin.matrix_edit_chain')}
            >
              ↗
            </Link>
          </>
        )}
      </td>
    </tr>
  );
}

function UnconfiguredRow({
  template,
  onSaved,
}: {
  template: TemplateView;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [type, setType] = useState('');
  const [dept, setDept] = useState('');
  const [form, setForm] = useState('');

  const save = useMutation({
    mutationFn: () =>
      templatesApi.update(template.id, {
        triggerType: type || null,
        triggerDept: dept || null,
        triggerForm: form || null,
      }),
    onSuccess: () => {
      onSaved();
      toast.success(t('admin.matrix_saved'));
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  return (
    <tr>
      <td>
        <div className="font-medium text-gray-900">{template.title}</div>
        <div className="text-[11px] text-muted">{t(`envelope.workflow_${template.workflowType}`)}</div>
      </td>
      <td>
        <ChainPreview template={template} />
      </td>
      <td className="text-right whitespace-nowrap">
        <div className="flex items-center gap-1 justify-end">
          <input
            className="text-[12px] border border-border rounded px-1.5 py-1 w-14"
            placeholder="INT"
            maxLength={12}
            value={type}
            onChange={(e) => setType(e.target.value.toUpperCase())}
          />
          <input
            className="text-[12px] border border-border rounded px-1.5 py-1 w-14"
            placeholder="GRC"
            maxLength={12}
            value={dept}
            onChange={(e) => setDept(e.target.value.toUpperCase())}
          />
          <input
            className="text-[12px] border border-border rounded px-1.5 py-1 w-14"
            placeholder="FRM"
            maxLength={12}
            value={form}
            onChange={(e) => setForm(e.target.value.toUpperCase())}
          />
          <button
            onClick={() => save.mutate()}
            disabled={(!type && !dept && !form) || save.isPending}
            className="btn btn-primary btn-sm"
          >
            {t('admin.matrix_add')}
          </button>
        </div>
      </td>
    </tr>
  );
}

function CodePill({ code }: { code: string | null }) {
  if (!code) return <span className="text-muted text-[11px]">—</span>;
  return (
    <code className="text-[11px] bg-gray-100 text-gray-900 rounded px-1.5 py-0.5 font-medium">
      {code}
    </code>
  );
}

function ChainPreview({ template }: { template: TemplateView }) {
  const { t } = useTranslation();
  const sorted = [...template.recipients].sort((a, b) => a.signingOrder - b.signingOrder);
  if (sorted.length === 0) {
    return <span className="text-[11px] text-muted">{t('admin.matrix_chain_empty')}</span>;
  }
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {sorted.map((r, i) => (
        <span key={r.id} className="flex items-center gap-1">
          <span
            className="text-[10px] bg-gray-100 text-gray-900 rounded-full px-2 py-0.5 font-medium"
            title={`${r.name} · ${r.role}`}
          >
            #{r.signingOrder} {r.name}
          </span>
          {i < sorted.length - 1 && <span className="text-muted text-[10px]">→</span>}
        </span>
      ))}
    </div>
  );
}
